import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatErr, downloadBlob } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { SectionCard } from "@/components/Primitives";
import { fmtMoney, fmtDateTime, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PlacementsPanel from "@/components/advertising/PlacementsPanel";
import AdvertiserFiles from "@/components/advertising/AdvertiserFiles";
import MonthlyContactDialog from "@/components/MonthlyContactDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, Globe, MapPin, Plus, PhoneCall, StickyNote, MapPinned,
  CalendarPlus, Building2, Clock, FileText, CheckSquare, TrendingUp, Trophy, Tag, Target, User, ClipboardCheck,
} from "lucide-react";

const TL = {
  call: { icon: PhoneCall, c: "#00C9E0" },
  contact: { icon: PhoneCall, c: "#00C9E0" },
  email: { icon: Mail, c: "#38BDF8" },
  meeting: { icon: CalendarPlus, c: "#A78BFA" },
  visit: { icon: MapPinned, c: "#9ACC32" },
  note: { icon: StickyNote, c: "#F59E0B" },
  task: { icon: CheckSquare, c: "#00C9E0" },
  opportunity: { icon: TrendingUp, c: "#FE3967" },
  sale: { icon: Trophy, c: "#5B880C" },
  contract: { icon: FileText, c: "#5B880C" },
  status_change: { icon: Clock, c: "#64748B" },
  assignment: { icon: User, c: "#64748B" },
};

function Timeline({ items }) {
  if (items.length === 0)
    return <p className="text-sm text-slate-400 py-8 text-center">No activity yet. Log a call or note to begin the timeline.</p>;
  return (
    <ol className="relative ml-3 space-y-5 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
      {items.map((t) => {
        const m = TL[t.type] || TL.note;
        const Ic = m.icon;
        return (
          <li key={t.id} className="relative ml-7" data-testid={`tl-${t.id}`}>
            <span className="absolute -left-[34px] flex h-7 w-7 items-center justify-center rounded-full ring-4 ring-white"
              style={{ background: `${m.c}22` }}>
              <Ic className="h-3.5 w-3.5" style={{ color: m.c }} />
            </span>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-slate-800">{t.title}</p>
              <span className="text-xs text-slate-400 whitespace-nowrap tnum">{fmtDateTime(t.created_at)}</span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              by {t.created_by_name}{t.meta?.outcome ? ` · ${t.meta.outcome}` : ""}{t.meta?.notes ? ` · ${t.meta.notes}` : ""}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 py-2">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-sm text-slate-700 truncate">{value || "—"}</p>
      </div>
    </div>
  );
}

export default function BusinessDetail() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const { id } = useParams();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [b, tl, ct, co, tk] = await Promise.all([
        api.get(`/businesses/${id}`),
        api.get(`/businesses/${id}/timeline`),
        api.get(`/contacts?business_id=${id}`),
        api.get(`/contracts?business_id=${id}`),
        api.get(`/tasks?business_id=${id}`),
      ]);
      setBiz(b.data); setTimeline(tl.data.items); setContacts(ct.data.items);
      setContracts(co.data.items); setTasks(tk.data.items);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openModal = (type) => { setForm({}); setModal(type); };
  const submit = async () => {
    try {
      if (modal === "call") await api.post("/calls", { business_id: id, ...form });
      if (modal === "note") await api.post("/notes", { business_id: id, text: form.text });
      if (modal === "visit") await api.post("/visits", { business_id: id, action: form.action || "Check In", ...form });
      if (modal === "task") await api.post("/tasks", { business_id: id, business_name: biz.name, ...form });
      if (modal === "meeting") await api.post("/events", { business_id: id, business_name: biz.name, type: "Meeting", ...form });
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  if (!biz) return <div className="space-y-4"><div className="h-28 rounded-xl bg-white border animate-pulse" /><div className="h-64 rounded-xl bg-white border animate-pulse" /></div>;

  const primary = contacts[0];
  const actions = [
    { key: "call", label: "Log Call", icon: PhoneCall }, { key: "note", label: "Add Note", icon: StickyNote },
    { key: "visit", label: "Log Visit", icon: MapPinned }, { key: "task", label: "Add Task", icon: Plus },
    { key: "meeting", label: "Schedule Meeting", icon: CalendarPlus },
  ];

  return (
    <div data-testid="page-business-detail">
      <button onClick={() => navigate("/businesses")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" />Businesses</button>

      {/* Record header */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm cc-fade-up">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#00C9E0] via-[#9ACC32] to-[#FE3967]" />
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 pt-1">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-[#1E2025]">{biz.name}</h1>
              <StatusChip value={biz.lifecycle} />
              {biz.priority && <StatusChip value={biz.priority} />}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-slate-500">
              {biz.phone && <a href={`tel:${biz.phone}`} className="flex items-center gap-1.5 hover:text-[#0094A8]"><Phone className="h-4 w-4" />{biz.phone}</a>}
              {biz.email && <a href={`mailto:${biz.email}`} className="flex items-center gap-1.5 hover:text-[#0094A8]"><Mail className="h-4 w-4" />{biz.email}</a>}
              {biz.website && <span className="flex items-center gap-1.5"><Globe className="h-4 w-4" />{biz.website}</span>}
              {biz.city && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{biz.city}, {biz.province}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {biz.phone && <Button size="sm" asChild data-testid="quick-call"><a href={`tel:${biz.phone}`}><Phone className="h-4 w-4" />Call</a></Button>}
            {biz.email && <Button size="sm" variant="outline" asChild data-testid="quick-email"><a href={`mailto:${biz.email}`}><Mail className="h-4 w-4" />Email</a></Button>}
            {biz.lifecycle === "Advertiser" && (
              <Button size="sm" variant="outline" onClick={() => setLogOpen(true)} data-testid="action-monthly-contact">
                <ClipboardCheck className="h-4 w-4" />Log Monthly Contact
              </Button>
            )}
            {actions.map((a) => (
              <Button key={a.key} size="sm" variant="outline" onClick={() => openModal(a.key)} data-testid={`action-${a.key}`}>
                <a.icon className="h-4 w-4" />{a.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3 mt-6">
        {/* Summary rail */}
        <SectionCard title="Summary" className="lg:col-span-1 h-fit">
          <div className="divide-y divide-slate-100">
            <InfoRow icon={User} label="Primary Contact" value={primary ? `${primary.first_name} ${primary.last_name}` : "—"} />
            <InfoRow icon={Target} label="Lead Source" value={biz.lead_source} />
            <InfoRow icon={Tag} label="Category" value={biz.category} />
            <InfoRow icon={MapPin} label="Market / Zone" value={[biz.territory, biz.zone].filter(Boolean).join(" › ")} />
            <InfoRow icon={TrendingUp} label="Potential Value" value={biz.potential_value ? fmtMoney(biz.potential_value) : "—"} />
            <InfoRow icon={Clock} label="Last Contacted" value={biz.last_contacted ? fmtDate(biz.last_contacted) : "—"} />
          </div>
        </SectionCard>

        {/* Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue={tabParam || "overview"}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="placements" data-testid="tab-placements">Placements &amp; Zones</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline">Activity</TabsTrigger>
              <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
              <TabsTrigger value="contracts">Contracts ({contracts.length})</TabsTrigger>
              <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
              <TabsTrigger value="files" data-testid="tab-files">Advertiser Files</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <SectionCard title="Recent Activity" action={timeline.length > 0 && <span className="text-xs text-slate-400">{timeline.length} total</span>}>
                <Timeline items={timeline.slice(0, 5)} />
              </SectionCard>
            </TabsContent>

            <TabsContent value="placements" className="mt-4">
              <PlacementsPanel businessId={id} onChanged={load} />
            </TabsContent>

            <TabsContent value="timeline" className="mt-4">
              <SectionCard><Timeline items={timeline} /></SectionCard>
            </TabsContent>

            <TabsContent value="contacts" className="mt-4">
              {contacts.length === 0 ? <SectionCard><p className="text-sm text-slate-400 py-6 text-center">No contacts yet.</p></SectionCard> : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {contacts.map((c) => (
                    <div key={c.id} className="cc-card cc-card-hover p-4" data-testid={`contact-card-${c.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-[#00C9E0]/15 text-[#0094A8] flex items-center justify-center text-sm font-bold shrink-0">
                            {(c.first_name?.[0] || "") + (c.last_name?.[0] || "")}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm text-slate-800 truncate">{c.first_name} {c.last_name}</p>
                            <p className="text-xs text-slate-400 truncate">{c.title || "Contact"}</p>
                          </div>
                        </div>
                        {contacts[0]?.id === c.id && <span className="text-[10px] font-bold uppercase text-[#5B880C] bg-[#9ACC32]/15 rounded px-1.5 py-0.5">Primary</span>}
                      </div>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        {c.email && <p className="truncate">{c.email}</p>}
                        {c.phone && <p>{c.phone}</p>}
                      </div>
                      <div className="mt-3 flex gap-2">
                        {c.phone && <Button size="sm" variant="outline" className="h-7 text-xs flex-1" asChild><a href={`tel:${c.phone}`}><Phone className="h-3 w-3" />Call</a></Button>}
                        {c.email && <Button size="sm" variant="outline" className="h-7 text-xs flex-1" asChild><a href={`mailto:${c.email}`}><Mail className="h-3 w-3" />Email</a></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="contracts" className="mt-4">
              <SectionCard>
                {contracts.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No contracts.</p> : (
                  <div className="divide-y divide-slate-100">
                    {contracts.map((c) => (
                      <div key={c.id} className="flex items-center justify-between py-3">
                        <div><p className="font-semibold text-sm">{c.contract_number} · {c.product_name}</p><p className="text-xs text-slate-400">{c.term} · <span className="tnum">{fmtMoney(c.contract_total)}</span></p></div>
                        <div className="flex items-center gap-2">
                          <StatusChip value={c.payment_status} /><StatusChip value={c.agreement_status} />
                          <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${c.id}`)} data-testid={`contract-view-${c.id}`}>View</Button>
                          <Button size="sm" variant="outline" onClick={() => downloadBlob(`/pdf/contract/${c.id}`, `contract_${c.contract_number}.pdf`)} data-testid={`contract-pdf-${c.id}`}>PDF</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            <TabsContent value="tasks" className="mt-4">
              <SectionCard>
                {tasks.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center">No tasks.</p> : (
                  <div className="divide-y divide-slate-100">
                    {tasks.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-3">
                        <div><p className="font-semibold text-sm">{t.title}</p><p className="text-xs text-slate-400">Due {fmtDate(t.due_date)}</p></div>
                        <StatusChip value={t.status} />
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </TabsContent>

            <TabsContent value="files" className="mt-4">
              <AdvertiserFiles businessId={id} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="activity-dialog">
          <DialogHeader><DialogTitle className="font-display capitalize">{modal === "task" ? "Add Task" : modal === "meeting" ? "Schedule Meeting" : `Log ${modal}`}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {modal === "call" && (<>
              <div><Label className="text-xs">Direction</Label>
                <Select value={form.direction || "Outgoing"} onValueChange={(v) => setForm({ ...form, direction: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Outgoing">Outgoing</SelectItem><SelectItem value="Incoming">Incoming</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Outcome</Label>
                <Select value={form.outcome || ""} onValueChange={(v) => setForm({ ...form, outcome: v })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select outcome" /></SelectTrigger><SelectContent>{["No Answer", "Left Voicemail", "Spoke With Contact", "Interested", "Call Back", "Meeting Booked", "Not Interested", "Wrong Number", "Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Notes</Label><Textarea className="mt-1" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </>)}
            {modal === "note" && <div><Label className="text-xs">Note</Label><Textarea className="mt-1" value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="note-text" /></div>}
            {modal === "visit" && (<>
              <div><Label className="text-xs">Action</Label><Select value={form.action || "Check In"} onValueChange={(v) => setForm({ ...form, action: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Check In">Check In</SelectItem><SelectItem value="Check Out">Check Out</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs">Result / Notes</Label><Textarea className="mt-1" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value, result: e.target.value })} /></div>
            </>)}
            {modal === "task" && (<>
              <div><Label className="text-xs">Title</Label><Input className="mt-1" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" className="mt-1" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label className="text-xs">Priority</Label><Select value={form.priority || "Medium"} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{["High", "Medium", "Low"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
            </>)}
            {modal === "meeting" && (<>
              <div><Label className="text-xs">Title</Label><Input className="mt-1" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="text-xs">Start</Label><Input type="datetime-local" className="mt-1" value={form.start || ""} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
              <div><Label className="text-xs">End</Label><Input type="datetime-local" className="mt-1" value={form.end || ""} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
            </>)}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModal(null)}>Cancel</Button><Button onClick={submit} data-testid="activity-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <MonthlyContactDialog
        open={logOpen}
        onOpenChange={setLogOpen}
        businessId={id}
        businessName={biz.name}
        contacts={contacts}
        onSaved={load}
      />
    </div>
  );
}
